import EmptyStateNotice from '@/components/EmptyStateNotice';
import Page from '@/components/page/Page';
import Button from '@/components/Button';
import { FiDownload, FiInfo, MdOutlineDashboardCustomize } from '@/components/react-icons/main/index.esm';
import IconV2 from '@/components/v2/IconV2';
import RowV2 from '@/components/v2/RowV2';
import ScrollContainerV2 from '@/components/v2/ScrollContainerV2';
import setQuery from '@/lib/setQuery';
import { exportWorkflowLogOperationLogs } from '@/redux/actions/gravityActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { query } from '@/redux/selectors/querySelectors.js';
import selectors from '@/redux/selectors/selectors';
import activityModels from '@/screens/home/schema/activityModels';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { getWorkflowOperationLogExportLabel, getWorkflowOperationLogExportTab } from '@/screens/workflows/workflowLogs/lib/operationLogExportTabs';
import LogWorkDoneRow from '@/screens/workflows/workflowLogs/LogWorkDoneRow';
import { groupBy } from '@shared/utils/groupBy.js';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { actionModels } from 'shared/schema/actionModels';

const MAX_LOG_ENTRIES = 100;

const getAppNameByGroup = ({ group, workflow, appsMetadata }) => {
  const firstItem = group?.[0];
  const step = workflow?.steps?.find((s) => s.id === firstItem?.stepId);
  const appMetadata = step ? appsMetadata?.find((a) => a.id === step?.appId) : null;
  return appMetadata?.name;
};

const LogWorkDone = () => {
  const activeLogId = useArgSelector(query, 'logId');
  const selectedTab = useArgSelector(query, 'logTab');
  const log = useArgSelector(selectors.workflowLogsById, activeLogId);
  const workflowId = useArgSelector(selectors.workflowId);
  const items = useArgSelector(selectors.workflowOperationLogsByLogId, activeLogId) || [];
  const isLoadingOperationLogs = useArgSelector(selectors.isLoading, 'getWorkflowLogOperationLogs');
  const isLoadingOutput = useArgSelector(selectors.isLoading, 'getWorkflowLogOutput');
  const exportTab = getWorkflowOperationLogExportTab(selectedTab);
  const exportLabel = getWorkflowOperationLogExportLabel(selectedTab);
  const isExporting = useArgSelector(selectors.isLoading, `exportWorkflowLogOperationLogs-${activeLogId}-${exportTab}`);

  const workflow = useSelector(workflowScreenSelectors.activeWorkflow);
  const appsMetadata = useSelector(selectors.appsMetadata);

  const isLoading = isLoadingOperationLogs || isLoadingOutput;

  const sortedItems = [...items].sort((a, b) => new Date(b.updatedOn) - new Date(a.updatedOn));

  const filteredItems = sortedItems
    ?.filter((item) => {
      if (selectedTab === 'errors') {
        return item.actionStatus === 'error';
      }
      if (selectedTab === 'warnings') {
        return item.actionStatus === 'warn';
      }
      if (selectedTab === 'successes') {
        return ['success', 'info'].includes(item.actionStatus);
      }
      return true;
    })
    .slice(0, MAX_LOG_ENTRIES);

  const groups = Object.values(groupBy(filteredItems, 'model'));

  const totalOperations = {
    errors: log.meta?.operationLogsByType?.error || 0,
    warnings: log.meta?.operationLogsByType?.warn || 0,
    successes: (log.meta?.operationLogsByType?.success || 0) + (log.meta?.operationLogsByType?.info || 0)
  };

  const handleExportCsv = (rawCsv) => {
    if (!rawCsv || typeof window === 'undefined') {
      return;
    }

    const blob = new Blob([rawCsv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `workflow-log-${activeLogId}-${exportTab}-operations.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportClick = () => {
    if (!workflowId || !activeLogId || !exportTab) {
      return;
    }

    exportWorkflowLogOperationLogs(
      {
        workflowId,
        workflowLogId: activeLogId,
        tab: exportTab
      },
      handleExportCsv
    );
  };

  const emptyStateCopyMap = {
    errors: {
      title: "You're good to go!",
      description: 'This workflow does not have any errors.'
    },
    warnings: {
      title: "You're good to go!",
      description: 'This workflow does not have any warnings.'
    },
    successes: {
      title: 'No work done',
      description: 'This workflow does not have any record of what was changed. Please view the Timeline for more information.',
      actions: [
        {
          label: 'View Timeline',
          onClick: () => setQuery({ logTab: 'timeline' })
        }
      ]
    }
  };

  return (
    <ScrollContainerV2 className={'log-work-done'}>
      <Page.Section>
        {groups?.map((group, idx) => (
          <Fragment key={idx}>
            {!isLoading && groups?.length > 0 && (
              <RowV2 className="row" variant={'grouping'}>
                <RowV2.Left>
                  {activityModels?.[group?.[0]?.model]?.()?.icon && <IconV2 icon={activityModels?.[group?.[0]?.model]?.()?.icon} />}
                  <div>
                    {getAppNameByGroup({ group, workflow, appsMetadata }) ? `${getAppNameByGroup({ group, workflow, appsMetadata })} - ` : ''}
                    {actionModels?.[group?.[0]?.model]?.nicknamePlural || 'General'}
                  </div>
                </RowV2.Left>
                {idx === 0 && (
                  <RowV2.Right>
                    <Button className="ghost log-work-done-export-button" size="sm" icon={FiDownload} title={isExporting ? 'Exporting...' : `Export ${exportLabel}`} onClick={handleExportClick} loading={isExporting} disabled={isLoading || isExporting || !activeLogId || totalOperations[selectedTab] === 0} />
                  </RowV2.Right>
                )}
              </RowV2>
            )}

            {!isLoading && (
              <div className={'group'}>
                {group.map((update) => (
                  <LogWorkDoneRow update={update} key={update?.id} />
                ))}
              </div>
            )}
          </Fragment>
        ))}
        {totalOperations[selectedTab] > MAX_LOG_ENTRIES && ( //
          <RowV2 className="row" variant={'grouping'}>
            <IconV2 icon={FiInfo} variant={'light-medium'} />
            {`${totalOperations[selectedTab] - MAX_LOG_ENTRIES} more records not shown (Data limited for performance)`}
          </RowV2>
        )}

        {isLoading && <EmptyStateNotice size={'sm'} loading card title={'Getting workflow results...'} />}
        {!isLoading && filteredItems.length === 0 && (
          <EmptyStateNotice //
            size={'sm'}
            icon={MdOutlineDashboardCustomize}
            loading={false}
            card
            title={emptyStateCopyMap[selectedTab]?.title}
            description={emptyStateCopyMap[selectedTab]?.description}
            actions={emptyStateCopyMap[selectedTab]?.actions}
          />
        )}
      </Page.Section>
    </ScrollContainerV2>
  );
};

export default LogWorkDone;
